import {
  useGridApiContext,
  useGridSelector,
  gridPageCountSelector,
  gridPageSelector,
  gridPageSizeSelector,
  gridRowCountSelector,
} from "@mui/x-data-grid";
import { IconButton, MenuItem, useMediaQuery } from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import InputField from "../input/input-component";

export function CustomGridPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  const pageSizeOptions = [10, 20, 30, 40, 50, 100];
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const handlePageChange = (newPage) => {
    apiRef.current.setPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    apiRef.current.setPageSize(event.target.value);
  };

  const displayPaginationInfo = `${page * pageSize + 1}-${Math.min((page + 1) * pageSize, rowCount)} of ${rowCount}`;

  return (
    <div className="flex justify-end items-center flex-wrap flex-grow w-full gap-4 md:gap-10 mt-4 md:mt-6">
      <div className="flex items-center gap-2 md:gap-4 w-full md:w-fit order-1 md:order-0 bg-grey-25 md:bg-transparent justify-center md:justify-normal rounded-2xl md:rounded-none">
        <p className="text-sm md:text-base text-black font-normal">
          {isMobile ? "Page" : "Items per page"}
        </p>

        <InputField
          select
          SelectProps={{
            IconComponent: (props) => <ExpandMoreRoundedIcon {...props} />,
            displayEmpty: true,
            value: pageSize,
            onChange: handlePageSizeChange,
            MenuProps: {
              PaperProps: {
                style: {
                  minWidth: "110px",
                },
              },
            },
          }}
        >
          {pageSizeOptions.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </InputField>
      </div>

      {!isMobile && (
        <p className="text-base text-black font-normal order-1">
          {displayPaginationInfo}
        </p>
      )}

      <div className="flex items-center w-full md:w-fit order-0 md:order-2 justify-between md:justify-normal">
        <IconButton
          onClick={() => handlePageChange(0)}
          disabled={page === 0}
          aria-label="first page"
          disableRipple
          className="hidden md:flex px-6 py-2 md:p-2 text-white md:text-black bg-grey-800 rounded-full md:bg-transparent disabled:bg-grey-600 disabled:text-white md:disabled:bg-transparent md:disabled:text-grey-300"
        >
          <FirstPageIcon />
        </IconButton>

        <IconButton
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 0}
          aria-label="previous page"
          disableRipple
          className="px-6 py-2 md:p-2 text-white md:text-black bg-grey-800 rounded-full md:bg-transparent disabled:bg-grey-600 disabled:text-white md:disabled:bg-transparent md:disabled:text-grey-300"
        >
          <NavigateBeforeIcon />
        </IconButton>

        {isMobile && (
          <p className="text-sm text-grey-600 font-normal">
            {displayPaginationInfo}
          </p>
        )}

        <IconButton
          onClick={() => handlePageChange(page + 1)}
          disabled={page >= pageCount - 1}
          aria-label="next page"
          disableRipple
          className="px-6 py-2 md:p-2 text-white md:text-black bg-grey-800 rounded-full md:bg-transparent disabled:bg-grey-600 disabled:text-white md:disabled:bg-transparent md:disabled:text-grey-300"
        >
          <NavigateNextIcon />
        </IconButton>

        <IconButton
          onClick={() => handlePageChange(pageCount - 1)}
          disabled={page >= pageCount - 1}
          aria-label="last page"
          disableRipple
          className="hidden md:flex px-6 py-2 md:p-2 text-white md:text-black bg-grey-800 rounded-full md:bg-transparent disabled:bg-grey-600 disabled:text-white md:disabled:bg-transparent md:disabled:text-grey-300"
        >
          <LastPageIcon />
        </IconButton>
      </div>
    </div>
  );
}
